import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import io from "socket.io-client";
import { GoDotFill } from "react-icons/go";
import toast from "react-hot-toast";
import SettingServices from "../../../services/SettingServices";

const DashboardMobileUser = ({ user, data }) => {
  const wallet = data?.wallet;
  const recentActivity = data?.recentActivity || [];
  const settings = data?.settings;

  const [homeNotes, setHomeNotes] = useState([]);
  const [gameStatus, setGameStatus] = useState({
    game_3min: true,
    game_5min: true,
  });

  useEffect(() => {
    if (
      settings &&
      (settings.game_3min !== undefined || settings.game_5min !== undefined)
    ) {
      setGameStatus((prev) => ({
        ...prev,
        ...(settings.game_3min !== undefined && {
          game_3min: !!settings.game_3min,
        }),
        ...(settings.game_5min !== undefined && {
          game_5min: !!settings.game_5min,
        }),
      }));
    }
  }, [data?.settings]);

  useEffect(() => {
    const s = io(process.env.REACT_APP_NODE_URL, { transports: ["websocket"] });
    s.on("gameStatusChange", (payload) => {
      if (
        payload &&
        (payload.game_3min !== undefined || payload.game_5min !== undefined)
      ) {
        setGameStatus((prev) => ({
          ...prev,
          ...(payload.game_3min !== undefined && {
            game_3min: !!payload.game_3min,
          }),
          ...(payload.game_5min !== undefined && {
            game_5min: !!payload.game_5min,
          }),
        }));
      }
    });
    return () => s.disconnect();
  }, []);

  useEffect(() => {
    const settingApi = new SettingServices();
    const loadHomeNotes = async () => {
      try {
        const res = await settingApi.getSettingData();
        const settings = res?.data ?? res?.data?.data ?? null;
        if (
          settings &&
          (settings.game_3min !== undefined || settings.game_5min !== undefined)
        ) {
          setGameStatus((prev) => ({
            ...prev,
            ...(settings.game_3min !== undefined && {
              game_3min: !!settings.game_3min,
            }),
            ...(settings.game_5min !== undefined && {
              game_5min: !!settings.game_5min,
            }),
          }));
        }
        const raw = settings?.home_notes;
        if (!raw) {
          setHomeNotes([]);
          return;
        }
        const parts = String(raw)
          .split("HomeNote")
          .map((p) => p.trim())
          .filter(Boolean);
        setHomeNotes(parts);
      } catch (err) {
        console.error("Error loading home notes:", err);
        setHomeNotes([]);
      }
    };
    loadHomeNotes();
  }, []);

  const balance = wallet ? Number(wallet.wallet_balance || 0) : 0;

  return (
    <div className="dashboard-mobile">
      {/* Hero: Wallet balance */}
      <div className="dashboard-mobile-hero">
        <div className="dashboard-mobile-hero-label">Wallet Balance</div>
        <div className="dashboard-mobile-hero-value">₹{balance.toFixed(2)}</div>
      </div>

      {/* Primary actions: full-width neon-style buttons (stacked); pass game type in URL */}
      <div className="dashboard-mobile-primary">
        <Link
          to="/mobile-live-session?game=3min"
          className="dashboard-mobile-primary-link"
          onClick={(e) => {
            if (!gameStatus.game_3min) {
              e.preventDefault();
              toast.error("3 Min game is stopped. Please try again later.");
            }
          }}
        >
          <span className="dashboard-mobile-primary-btn-wrap">
            <span className="dashboard-mobile-primary-btn">
              Play 3 Min Yantra{" "}
              <span className={gameStatus.game_3min ? "live" : "stopped"}>
                <GoDotFill />
                {gameStatus.game_3min ? "Live" : "Stopped"}
              </span>
            </span>
          </span>
        </Link>
        <Link
          to="/mobile-live-session?game=5min"
          className="dashboard-mobile-primary-link"
          onClick={(e) => {
            if (!gameStatus.game_5min) {
              e.preventDefault();
              toast.error("5 Min game is stopped. Please try again later.");
            }
          }}
        >
          <span className="dashboard-mobile-primary-btn-wrap">
            <span className="dashboard-mobile-primary-btn">
              Play 5 Min Yantra{" "}
              <span className={gameStatus.game_5min ? "live" : "stopped"}>
                <GoDotFill />
                {gameStatus.game_5min ? "Live" : "Stopped"}
              </span>
            </span>
          </span>
        </Link>
      </div>

      {/* Home Notes (from settings), split by 'HomeNote' */}
      {homeNotes.length > 0 && (
        <div className="dashboard-mobile-home-notes">
          <div className="dashboard-mobile-home-notes-list">
            {homeNotes.map((note, idx) => (
              <p key={idx} className="dashboard-mobile-home-note">
                {note}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Secondary actions: smaller buttons in a row */}
      <div className="dashboard-mobile-secondary">
        <Link
          to="/transaction-history"
          className="dashboard-mobile-secondary-btn"
        >
          Transaction History
        </Link>
        <Link
          to="/withdrawal-management"
          className="dashboard-mobile-secondary-btn"
        >
          Withdrawal
        </Link>
        <Link
          to="/profile"
          className="dashboard-mobile-secondary-btn dashboard-mobile-secondary-btn-alt"
        >
          My Profile
        </Link>
      </div>

      {/* Recent activity */}
      <div className="dashboard-mobile-activity">
        <h2 className="dashboard-mobile-activity-title">Recent Activity</h2>
        <div className="activity-list">
          {recentActivity.length === 0 ? (
            <p className="activity-empty">No recent activity.</p>
          ) : (
            recentActivity.map((a) => (
              <div className="activity-item" key={a.id}>
                <span className="activity-time">
                  {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}
                </span>
                <span className="activity-desc">
                  Session #{a.session_no} ({a.game_type}) — {a.winner_type}
                </span>
                <span
                  className={`activity-result ${
                    Number(a.winning_amount || 0) > 0 ? "won" : "lost"
                  }`}
                >
                  {Number(a.winning_amount || 0) > 0 ? "₹" : ""}
                  {Number(a.winning_amount || 0).toFixed(2)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardMobileUser;
